import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "../../ui/Select/Select";
import { Button } from "../../ui/Button/Button";
import { useCustomer } from "../../../contexts/CustomerContext";
import { useAuth } from "../../../contexts/AuthContext";
import { ROUTES } from "../../../constants/routes";
import { cn } from "../../../lib/cn";

/**
 * CustomerSwitcher Component
 * Dropdown in header for switching between accessible customers
 * Only shown for consultants and admins (not simple users)
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 */
export function CustomerSwitcher({ className }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedCustomer, accessibleCustomers, switchCustomer, loading } =
    useCustomer();

  const [switching, setSwitching] = useState(false);

  // Debug logging

  // Don't show switcher for simple users
  if (user?.role === "user") {
    return null;
  }

  // Don't show if no customers available
  if (!accessibleCustomers || accessibleCustomers.length === 0) {
    return null;
  }

  // Show dropdown for all consultants/admins (even with 1 customer)
  /**
   * Handle customer switch
   */
  const handleCustomerSwitch = async (e) => {
    const customerId = e.detail.selectedOption.value;

    if (customerId === selectedCustomer?.id) {
      return; // Already selected
    }

    if (customerId === "__view_all__") {
      // Navigate to customer selection page
      navigate(ROUTES.SELECT_CUSTOMER);
      return;
    }

    try {
      setSwitching(true);
      await switchCustomer(customerId);
    } catch (error) {
      console.error("Failed to switch customer:", error);
    } finally {
      setSwitching(false);
    }
  };

  /**
   * Navigate to customer selection page
   */
  const handleViewAll = () => {
    navigate(ROUTES.SELECT_CUSTOMER);
  };

  // Prepare options for select
  const options = [
    ...accessibleCustomers.map((customer) => ({
      value: customer.id,
      label: customer.name,
    })),
    // Add "View All Customers" option
    {
      value: "__view_all__",
      label: "→ View All Customers",
    },
  ];

  return (
    <div className={cn("flex items-center gap-3", className)}>
      {/* Label */}
      <span className="text-sm text-gray-600 hidden sm:inline">Customer:</span>

      {/* Select Dropdown */}
      <div className="min-w-[200px] max-w-[300px]">
        <Select
          key={selectedCustomer?.id || "no-customer"} // Force re-render when customer changes
          value={selectedCustomer?.id || accessibleCustomers[0]?.id}
          onChange={handleCustomerSwitch}
          options={options}
          disabled={switching || loading}
          className="w-full"
        />
      </div>

      {/* Customer count badge */}
      {accessibleCustomers.length > 1 && (
        <span className="text-xs text-gray-500 hidden lg:inline">
          ({accessibleCustomers.length} available)
        </span>
      )}
    </div>
  );
}
